import React, { useEffect, useState } from "react";
import { Row, Col, Container, Stack, Button, Spinner, Card } from "react-bootstrap";
import { useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { fetchRoles, fetchSupportTypes, fetchUser, fetchUserCases, logout, patchUser, postUser } from "../../services/httpClient";
import Input from "../ui/Input";
import InputPhone from "../ui/InputPhone";
import PageTitle from "../ui/PageTitle";
import SelectObject from "../ui/SelectObject";
import ToastMsg from "../ui/ToastMsg";
import { Case } from "../../types/case";
import SaveChangesModal from "../ui/SaveChangesModal";
import useApp from "../../utils/useApp";
import { unsavedChanges, currentUrl } from "../../App";
import CaptureProfilePic from "../ui/CaptureProfilePic";

const EditUser = () => {
  const location = useLocation();
  currentUrl.value = location.pathname;
  const { id } = useParams();
  const [profile] = useSearchParams();
  const idAsNumber = id && /^\d+$/.test(id) ? parseInt(id, 10) : null;
  const { auth, setAuth, showMenu, setActivePage }: any = useApp();
  const [editMode, setEditMode] = useState(id !== undefined ? true : false);
  const [isMyUserProfile] = useState(auth?.profile?.id === idAsNumber ? true : false);
  const [role, setRole] = useState<string>();
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [preferredName, setPreferredName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [phone, setPhone] = useState<number | null>(null);
  const [profileId, setProfileId] = useState<string>();
  const [profileImage, setProfileImage] = useState<string>("");
  const [organization, setOrganization] = useState<string>("");
  const [supportType, setSupportType] = useState<string>("");
  const [twofactorSetup, setTwoFactorSetup] = useState<boolean>(false);
  const [cases, setCases] = useState<Case[]>([]);
  const [saveIsEnabled, setSaveIsEnabled] = useState<boolean>(false);
  const [isValidEmail, setIsValidEmail] = useState<boolean>(true);
  const [isValidPhone, setIsValidPhone] = useState<boolean>(true);
  const [errMsg, setErrMsg] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastBody, setToastBody] = useState<string>("");
  const [toastText, setToastText] = useState<string>("");
  const [isDisabling, setIsDisabling] = useState(false);
  const [isTwoFactorDisabled, setIsTwoFactorDisabled] = useState(false);
  const heading = editMode ? "Edit User" : "Create User";
  const [roles, setRoles] = useState<any[]>([]);
  const [supportTypes, setSupportTypes] = useState<any[]>([]);
  const [currentDescText, setCurrentDescText] = useState<string>("");
  const [isFieldsDisabled, setIsFieldsDisabled] = useState(false);
  const default2fa = auth?.profile?.default_2fa;
  const [homepage, setHomepage] = useState<string>("");
  const activeCases = cases.filter((c: any) => c.casestatus === "active");
  const waitlistCases = cases.filter((c: any) => c.casestatus === "waitlist");
  const inactiveCases = cases.filter((c: any) => c.casestatus === "inactive");

  let navigate = useNavigate();

  const backUser = (e: any) => {
    if (saveIsEnabled) {
      setShowModal(true);
    } else {
      navigate(`/users`);
    }
  };

  useEffect(() => {
    if (id !== undefined) {
      setEditMode(true);
    }
    setIsLoading(true);
    getRoles();
    if (id === undefined) {
      setRole(location.pathname.split("/")[4]);
    }
    getSupportTypes();
    setCurrentDescText(`Org Admin - Access Admin plus Organization
Admin - Access Case Manager plus Users, Cases, and Reports
Case Manager - Access to My Cases, Groups, and Announcements
${auth?.profile?.includeHMIS && `Residential Support - Access to Cases and Shelter`}
Support - Access to Messaging in My Cases
Primary Adults - Access to their own case`);
    if (id !== undefined) {
      getUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (saveIsEnabled) {
      unsavedChanges.value = true;
    } else {
      unsavedChanges.value = false;
    }
  }, [saveIsEnabled]);

  useEffect(() => {
    if (id !== undefined) {
      getUser();
    }
  }, [profileImage]);

  const getRoles = async () => {
    await fetchRoles(auth?.profile?.includeHMIS ? true : false).then((res: any) => {
      var roleList = res[0].data;
      //remove family role
      roleList = roleList.filter((r: any) => r.key !== "family");

      //if !org_admin do not allow role to change user to an org_admin
      if (auth?.profile?.role !== "org_admin") {
        roleList = roleList.filter((r: any) => r.key !== "org_admin");
      }

      //if case manager only see that role
      if (auth?.profile?.role === "case_manager") {
        roleList = roleList.filter((r: any) => r.key === "case_manager");
      }

      //if support only see that role
      if (auth?.profile?.role === "support") {
        roleList = roleList.filter((r: any) => r.key === "support");
      }

      setRoles(roleList);
    });
  };

  const getSupportTypes = () => {
    fetchSupportTypes().then((res: any) => {
      setSupportTypes(res[0].data);
      if (!editMode) {
        setIsLoading(false);
      }
    });
  };

  function validateEmail(email: string) {
    var regexp = /\S+@\S+\.\S+/;
    return regexp.test(email);
  }

  function validatePhone(phone: string) {
    let length = phone.length;
    return length === 11;
  }

  const getUser = async () => {
    await fetchUser(id)
      .then((res: any) => {
        setRole(res[0].data.role);
        setFirstName(res[0].data.firstname);
        setLastName(res[0].data.lastname);
        setPreferredName(res[0].data.preferredname);
        setEmail(res[0].data.email);
        setPhone(res[0].data.phonenumber === "" ? null : res[0].data.phonenumber);
        setProfileId(res[0].data.profile_id);
        setOrganization(res[0].data.organization);
        setSupportType(res[0].data.supporttype);
        setTwoFactorSetup(res[0].data.twofactorSetup);
        setHomepage(res[0].data.homepage);

        if (res[0].data?.profile.length > 0) {
          res[0].data?.profile?.map((p: any) => {
            const base64String = "data:image/jpeg;base64, " + p?.data;
            setProfileImage(base64String);
          });
        } else {
          setProfileImage("");
        }

        if (res[0].data.role !== "res_support") {
          getUserCases(res[0].data.id, res[0].data.role);
        }

        //non org admins cannot edit org admins
        if (auth?.profile?.role !== "org_admin" && res[0].data?.role === "org_admin") {
          setIsFieldsDisabled(true);
        }

        setIsLoading(false);
      })
      .catch((err: any) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  const getUserCases = (id: number, role: string) => {
    fetchUserCases().then((res: any) => {
      var cases = res[0].data;
      var filteredCases: any[] = [];

      if (role === "support") {
        cases.forEach((c: any) => {
          c.support.forEach((s: any) => {
            if (s.user_id === id) {
              filteredCases = [...filteredCases, c];
            }
          });
        });
      } else if (role === "family") {
        cases.forEach((c: any) => {
          c.family.forEach((s: any) => {
            if (s.user_id === id) {
              filteredCases = [...filteredCases, c];
            }
          });
        });
      } else {
        filteredCases = cases.filter((c: any) => c.primaryCM?.user_id === id || c.secondaryCM?.user_id === id);
      }

      setCases(filteredCases);
    });
  };

  const saveUser = () => {
    setIsLoading(true);
    setErrMsg("");

    if (firstName && lastName && isValidEmail && isValidPhone) {
      const updatedUser = {
        role: role,
        email: email,
        firstname: firstName,
        lastname: lastName,
        preferredname: preferredName,
        phonenumber: phone,
        appaccess: true,
        organization: organization,
        //supporttype: supportType,
        homepage: homepage,
      };

      if (editMode) {
        patchUser(updatedUser, id)
          .then((res: any) => {
            setSaveIsEnabled(false);
            setShowToast(true);
            setToastText("User Updated");
            const toast = preferredName ? firstName + " '" + preferredName + "' " + lastName : firstName + " " + lastName;
            setToastBody(toast);

            if (isMyUserProfile) {
              setAuth((prevAuth: any) => ({
                ...prevAuth,
                profile: {
                  ...prevAuth.profile,
                  firstname: firstName,
                  preferredname: preferredName,
                },
              }));
            }
          })
          .catch((err: any) => {
            setIsLoading(false);
            setErrMsg(err?.response?.data?.msg);
          })
          .finally(() => {
            setIsLoading(false);
          });
      } else {
        postUser(updatedUser)
          .then((res: any) => {
            setSaveIsEnabled(false);
            setShowToast(true);
            setToastText("User Created");
            const toast = preferredName ? firstName + " '" + preferredName + "' " + lastName : firstName + " " + lastName;
            setToastBody(toast);
            clearUserState();
          })
          .catch((err: any) => {
            setIsLoading(false);
            setErrMsg(err?.response?.data?.msg);
          })
          .finally(() => {
            setIsLoading(false);
          });
      }
    } else if (!firstName || !lastName) {
      setIsLoading(false);
      setErrMsg("First and Last Name are Required");
    } else {
      setIsLoading(false);
      setErrMsg("Email or Phone is in Invalid Format");
    }
  };

  const clearUserState = () => {
    setFirstName("");
    setLastName("");
    setPreferredName("");
    setEmail("");
    setPhone(null);
    setOrganization("");
    setSupportType("");
  };

  function enableSave() {
    if (firstName && lastName && role && email && phone && isValidEmail && isValidPhone) {
      setSaveIsEnabled(true);
    } else {
      setSaveIsEnabled(false);
    }
  }

  const onClickDisable = () => {
    setIsDisabling(true);
  };

  const disableTwoFactor = () => {
    setIsLoading(true);

    const updatedUser = {
      totpsecret: "delete",
    };

    patchUser(updatedUser, id)
      .then((res: any) => {
        setIsDisabling(false);
        setShowToast(true);
        setToastText("User Updated");
        const toast = "Two-Factor Reset";
        setToastBody(toast);
        setIsTwoFactorDisabled(true);

        if (isMyUserProfile) {
          logout()
            .then((res: any) => {
              navigate("/login");
              setTwoFactorSetup(false);
              setIsTwoFactorDisabled(false);
            })
            .catch((err: any) => {
              console.log(err);
            })
            .finally(() => {
              setIsLoading(false);
            });
        } else {
          setTimeout(() => {
            setTwoFactorSetup(false);
            setIsTwoFactorDisabled(false);
            setIsLoading(false);
          }, 3000);
        }
      })
      .catch((err: any) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  const onClickCancelDisable = () => {
    setIsDisabling(false);
  };

  const updateRole = (e: any) => {
    setRole(e.target.value);
    if (!editMode) {
      navigate(`/users/user/create/${e.target.value}`);
    }

    if (e.target.value && firstName && lastName && email && phone && isValidEmail && isValidPhone) {
      setSaveIsEnabled(true);
    } else {
      setSaveIsEnabled(false);
    }
  };

  const updateFirstName = (e: any) => {
    setFirstName(e.target.value);
    if (e.target.value !== "" && lastName && email && phone && isValidEmail && isValidPhone) {
      setSaveIsEnabled(true);
    } else {
      setSaveIsEnabled(false);
    }
  };

  const updateLastName = (e: any) => {
    setLastName(e.target.value);
    if (e.target.value !== "" && lastName && email && phone && isValidEmail && isValidPhone) {
      setSaveIsEnabled(true);
    } else {
      setSaveIsEnabled(false);
    }
  };

  const updatePreferredName = (e: any) => {
    setPreferredName(e.target.value);
    enableSave();
  };

  const updateEmail = (e: any) => {
    setEmail(e.target.value);

    let validEmail = validateEmail(e.target.value);
    if (validEmail) {
      setIsValidEmail(true);
      if (isValidPhone) {
        if (firstName && lastName && role && phone) {
          setSaveIsEnabled(true);
        } else {
          setSaveIsEnabled(false);
        }
      }
    } else {
      setIsValidEmail(false);
      setSaveIsEnabled(false);
    }
  };

  const updatePhone = (e: any) => {
    setPhone(e);
    let validPhone = validatePhone(e);
    if (validPhone) {
      setIsValidPhone(true);
      if (isValidEmail) {
        if (firstName && lastName && role && email) {
          setSaveIsEnabled(true);
        } else {
          setSaveIsEnabled(false);
        }
      }
    } else {
      setIsValidPhone(false);
      setSaveIsEnabled(false);
    }
  };

  const updateOrganization = (e: any) => {
    setOrganization(e.target.value);
    enableSave();
  };

  const updateSupportType = (e: any) => {
    setSupportType(e.target.value);
    enableSave();
  };

  const getHomepages = () => {
    const includeHMIS = auth?.profile?.includeHMIS;
    let homepageOptions: { key: string; name: string }[] = [];

    if (role === "org_admin") {
      homepageOptions = [
        { key: "/settings", name: "Organization" },
        { key: "/my-cases", name: "My Cases" },
        { key: "/users", name: "Users" },
        { key: "/cases", name: "Cases" },
        { key: "/groups", name: "Groups" },
        { key: "/announcements", name: "Announcement" },
        { key: "/shelter", name: "Shelter" },
        { key: "/reports", name: "Reports" },
      ];
    } else if (role === "admin") {
      homepageOptions = [
        { key: "/my-cases", name: "My Cases" },
        { key: "/users", name: "Users" },
        { key: "/cases", name: "Cases" },
        { key: "/groups", name: "Groups" },
        { key: "/announcements", name: "Announcement" },
        { key: "/shelter", name: "Shelter" },
        { key: "/reports", name: "Reports" },
      ];
    } else if (role === "case_manager") {
      homepageOptions = [
        { key: "/my-cases", name: "My Cases" },
        { key: "/groups", name: "Groups" },
        { key: "/announcements", name: "Announcement" },
        { key: "/shelter", name: "Shelter" },
      ];
    } else if (role === "res_support") {
      homepageOptions = [
        { key: "/my-cases", name: "My Cases" },
        { key: "/cases", name: "Cases" },
        { key: "/shelter", name: "Shelter" },
      ];
    }

    if (includeHMIS !== 1) {
      homepageOptions = homepageOptions.filter((page) => page.key !== "/shelter");
    }

    return homepageOptions;
  };

  const updateHomepage = (e: any) => {
    setHomepage(e.target.value);

    if (isMyUserProfile) {
      setAuth((prevAuth: any) => ({
        ...prevAuth,
        profile: {
          ...prevAuth.profile,
          homepage: e.target.value,
        },
      }));
    }
    enableSave();
  };

  const renderDisableTwoFactor = () => {
    return (
      <Row>
        <Stack direction="horizontal" gap={3} style={{ paddingTop: "1rem" }}>
          <Stack style={{ justifyContent: "center" }}>
            <Card.Subtitle>
              Are you sure you want to reset two-factor for {firstName} {lastName}? {isMyUserProfile ? "This will log you out." : ""}
            </Card.Subtitle>
          </Stack>
          <Button className="app-danger-color button" onClick={() => disableTwoFactor()}>
            Confirm Reset
          </Button>
          <div className="vr" />
          <Button className="app-primary-color button" onClick={() => onClickCancelDisable()}>
            Cancel Reset
          </Button>
        </Stack>
      </Row>
    );
  };

  return (
    <>
      <ToastMsg showToast={showToast} setShowToast={setShowToast} toastText={toastText} toastBody={toastBody} />

      {isLoading ? (
        <Container className={showMenu ? "lg div-main-menu" : "lg div-main"}>
          <Button size="sm" className="spinner-button spinner-button-main">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
          </Button>
        </Container>
      ) : (
        <Container className={showMenu ? "lg div-main-menu" : "lg div-main"}>
          <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
            {errMsg}
          </p>
          {profile.get("profile") !== "true" ? (
            <>
              <Row className="mb-3">
                <Col xs={6} className="text-start">
                  <Button className={"app-primary-color button"} onClick={(e) => backUser(e)}>
                    {"Back to Users"}
                  </Button>
                </Col>
                <Col xs={6} className="text-end">
                  {!isFieldsDisabled && (
                    <Button id="pagetitle-button" className={"app-secondary-color button"} onClick={saveUser} disabled={!saveIsEnabled}>
                      {"Save"}
                    </Button>
                  )}
                </Col>
              </Row>
              <PageTitle title={heading} descriptionText={currentDescText} />
            </>
          ) : (
            <PageTitle
              title={heading}
              buttonTitle="Save"
              onButtonClick={saveUser}
              buttonDisabled={!saveIsEnabled}
              buttonColor="secondary"
            />
          )}
          <Row className="h-auto">
            <Col xs={12} lg={6}>
              <Stack gap={3}>
                <Row>
                  <Col xs={12}>
                    <SelectObject
                      label="Role"
                      value={role}
                      options={
                        isFieldsDisabled
                          ? [
                              {
                                id: 5,
                                name: "Org Admin",
                                key: "org_admin",
                              },
                            ]
                          : roles
                      }
                      skipInitialOption={true}
                      onUpdate={(e: any) => updateRole(e)}
                      disabled={isFieldsDisabled}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs={6}>
                    <Input
                      label="First Name"
                      type="text"
                      value={firstName}
                      onUpdate={(e: any) => updateFirstName(e)}
                      required={true}
                      disabled={isFieldsDisabled}
                      hasFocus={!editMode}
                    />
                  </Col>
                  <Col xs={6}>
                    <Input
                      label="Last Name"
                      type="text"
                      value={lastName}
                      onUpdate={(e: any) => updateLastName(e)}
                      required={true}
                      disabled={isFieldsDisabled}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs={6}>
                    <Input
                      label="Email"
                      type="email"
                      value={email}
                      onUpdate={(e: any) => updateEmail(e)}
                      required={true}
                      disabled={isFieldsDisabled}
                    />
                  </Col>
                  <Col xs={6}>
                    <InputPhone
                      label="Phone"
                      value={phone}
                      onUpdate={(e: any) => updatePhone(e)}
                      required={true}
                      disabled={isFieldsDisabled}
                    />
                  </Col>
                </Row>
                {role === "support" && (
                  <Row>
                    <Col xs={6}>
                      <SelectObject
                        label="Support Type"
                        value={supportType}
                        options={supportTypes}
                        onUpdate={(e: any) => updateSupportType(e)}
                      />
                    </Col>
                    <Col xs={6}>
                      <Input label="Organization" type="text" value={organization} onUpdate={(e: any) => updateOrganization(e)} />
                    </Col>
                  </Row>
                )}
                <Row>
                  <Col xs={6}>
                    <Input
                      label="Preferred Name"
                      type="text"
                      value={preferredName}
                      onUpdate={(e: any) => updatePreferredName(e)}
                      disabled={isFieldsDisabled}
                    />
                  </Col>
                  {role !== "support" && (
                    <Col xs={6}>
                      <SelectObject
                        label="Homepage"
                        value={homepage}
                        skipInitialOption={homepage ? true : false}
                        options={getHomepages()}
                        onUpdate={(e: any) => updateHomepage(e)}
                      />
                    </Col>
                  )}
                  {role !== "family" &&
                    !isFieldsDisabled &&
                    editMode &&
                    (twofactorSetup ? (
                      isDisabling ? (
                        <>{renderDisableTwoFactor()}</>
                      ) : (
                        <Col xs={6} className="align-self-center mt-3">
                          <Button className="app-tertiary-color mb-3 px-3 button" onClick={(e) => onClickDisable()}>
                            {default2fa === "app"
                              ? "Reset Two-Factor App Authentication"
                              : default2fa === "phone"
                                ? "Reset Two-Factor SMS Authentication"
                                : default2fa === "email"
                                  ? "Reset Two-Factor Email Authentication"
                                  : "Reset Two-Factor Authentication"}
                          </Button>
                        </Col>
                      )
                    ) : (
                      <Col xs={6} className="align-self-center mt-3">
                        <h5 className="m-0 p-0 successmsg ">Two-Factor Not Yet Configured</h5>
                      </Col>
                    ))}
                </Row>
              </Stack>
            </Col>
            <Col xs={6} lg={5} xl={4} className="mb-3">
              {editMode && (
                <CaptureProfilePic
                  userId={id}
                  userProfileId={profileId}
                  userName={firstName + " " + lastName}
                  userType="user"
                  profileImage={profileImage}
                  setProfileImage={setProfileImage}
                  setErrMsg={setErrMsg}
                  setShowToast={setShowToast}
                  setToastText={setToastText}
                  setToastBody={setToastBody}
                />
              )}
            </Col>
          </Row>
          {editMode && role !== "res_support" && (
            <>
              {cases.length > 0 ? (
                <>
                  <h4 className="app-header-text mt-2 mb-3">User Cases</h4>
                  {activeCases.length > 0 && (
                    <Row className="pb-3">
                      <h6 className="app-orange-text">Active Cases</h6>
                      {activeCases.map((c: any) => (
                        <Col key={c.id} xs={6} sm={4} lg={3} className="mb-2">
                          <Card
                            key={c.name}
                            className="bg-light"
                            style={{ cursor: "pointer" }}
                            onClick={() => (navigate(`/my-cases/${c.id}`), setActivePage("/my-cases"))}
                          >
                            <Card.Body>
                              <Stack direction="horizontal" gap={3}>
                                <Stack gap={2}>
                                  <Card.Subtitle>{c.name}</Card.Subtitle>
                                  <Card.Subtitle className="text-muted">{c.status}</Card.Subtitle>
                                </Stack>
                              </Stack>
                            </Card.Body>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  )}
                  {waitlistCases.length > 0 && (
                    <Row className="pb-3">
                      <h6 className="app-orange-text">Waitlist Cases</h6>
                      {waitlistCases.map((c: any) => (
                        <Col key={c.id} xs={6} sm={4} lg={3} className="mb-2">
                          <Card
                            key={c.name}
                            className="bg-light"
                            style={{ cursor: "pointer" }}
                            onClick={() => (navigate(`/my-cases/${c.id}`), setActivePage("/my-cases"))}
                          >
                            <Card.Body>
                              <Stack direction="horizontal" gap={3}>
                                <Stack gap={2}>
                                  <Card.Subtitle>{c.name}</Card.Subtitle>
                                  <Card.Subtitle className="text-muted">{c.status}</Card.Subtitle>
                                </Stack>
                              </Stack>
                            </Card.Body>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  )}
                  {inactiveCases.length > 0 && (
                    <Row className="pb-3">
                      <h6 className="app-orange-text">Inactive Cases</h6>
                      {inactiveCases.map((c: any) => (
                        <Col key={c.id} xs={6} sm={4} lg={3} className="mb-2">
                          <Card
                            key={c.name}
                            className="bg-light"
                            style={{ cursor: "pointer" }}
                            onClick={() => (navigate(`/my-cases/${c.id}`), setActivePage("/my-cases"))}
                          >
                            <Card.Body>
                              <Stack direction="horizontal" gap={3}>
                                <Stack gap={2}>
                                  <Card.Subtitle>{c.name}</Card.Subtitle>
                                  <Card.Subtitle className="text-muted">{c.status}</Card.Subtitle>
                                </Stack>
                              </Stack>
                            </Card.Body>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  )}
                </>
              ) : (
                <h6 className="mt-2">No User Cases</h6>
              )}
            </>
          )}
        </Container>
      )}
      {isTwoFactorDisabled && isMyUserProfile && (
        <Row className="pt-5 mt-5">
          <Col>
            <h4 className="p-3 text-center successmsg ">Logging Out!</h4>
          </Col>
        </Row>
      )}
      <SaveChangesModal showModal={showModal} setShowModal={setShowModal} path="/users" />
    </>
  );
};

export default EditUser;
